import { motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "../../../../utils/classnames";
import { SERVICE_CONFIG, SERVICE_KEYS, TIER_VIP } from "../constants";
import { formatYen, getTierRates } from "../pricing";
import { SwapText, usePressMotion } from "./animated";
import { FieldLabel, RadioMark, selectableCardClass } from "./fields";
import { Divider, NoteList } from "./layout";

const SMALL_TEXT_CLASS = "tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18.5px]";
const BODY_TEXT_CLASS = "tw:font-hanken-grotesk tw:text-[14px] tw:leading-[20px] tw:!text-[#4E4E4E]";

/** "21,500 JPY", or the two-line first guest / additional guest price. */
const TierPriceLines = ({ rates }) => {
  const { t } = useTranslation();
  if (!rates.additionalGuestPrice) return <SwapText value={formatYen(rates.firstGuestPrice)} />;
  return (
    <>
      <span className="tw:block">
        {t("meetAssistInquiry.Tiers.FirstGuest", { price: formatYen(rates.firstGuestPrice) })}
      </span>
      <span className="tw:block">
        {t("meetAssistInquiry.Tiers.AdditionalGuest", { price: formatYen(rates.additionalGuestPrice) })}
      </span>
    </>
  );
};

/** What the staff do, plus the VIP luggage caveat where it applies. */
const TierDescription = ({ serviceKey, tier }) => {
  const { t } = useTranslation();
  const isConnecting = serviceKey === SERVICE_KEYS.airportConnecting;
  const hasLuggageNote = tier === TIER_VIP && !isConnecting;

  return (
    <div className="tw:flex tw:flex-col tw:gap-y-5 tw:text-left">
      <p className={cn(BODY_TEXT_CLASS, "tw:!mb-0")}>
        {t(`meetAssistInquiry.TierDescriptions.${serviceKey}.${tier}`)}
      </p>
      {hasLuggageNote && (
        <div className="tw:flex tw:flex-col tw:gap-y-[6px]">
          <span className={cn(SMALL_TEXT_CLASS, "tw:font-bold tw:text-[#8D8D8D]")}>
            {t("meetAssistInquiry.Tiers.LuggageTitle")}
          </span>
          <p className={cn(BODY_TEXT_CLASS, "tw:!mb-0")}>
            <Trans i18nKey="meetAssistInquiry.Tiers.LuggageBody" components={{ strong: <strong /> }} />
          </p>
        </div>
      )}
      {isConnecting && (
        <div className="tw:flex tw:flex-col tw:gap-y-[6px]">
          <span className={cn(SMALL_TEXT_CLASS, "tw:font-bold tw:text-[#8D8D8D]")}>
            {t("meetAssistInquiry.Tiers.CancellationTitle")}
          </span>
          <NoteList
            className={cn(SMALL_TEXT_CLASS, "tw:!p-4 tw:!gap-y-[3px]")}
            notes={t("meetAssistInquiry.Tiers.ConnectingCancellation", { returnObjects: true })}
          />
        </div>
      )}
    </div>
  );
};

/** Selectable tier card (Standard or VIP) for services that offer both. */
const SelectableTierCard = ({ serviceKey, tier, rates, isSelected, onSelect, className }) => {
  const { t } = useTranslation();
  const pressMotion = usePressMotion(1, 0.99);
  return (
    <motion.button
      {...pressMotion}
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
      className={cn(
        selectableCardClass(isSelected, "tw:border-[#E8E8E8]"),
        "tw:flex tw:flex-col tw:gap-y-[22px] tw:p-5 tw:lg:p-6 tw:text-left",
        className
      )}
    >
      <span className="tw:flex tw:items-center tw:gap-x-4 tw:min-h-[62px]">
        <RadioMark isSelected={isSelected} />
        <span className="tw:flex tw:flex-col tw:gap-y-[3px] tw:font-hanken-grotesk">
          <span className={cn("tw:text-[15px] tw:leading-[22px] tw:text-[#282828]", isSelected && "tw:font-bold")}>
            {t(`meetAssistInquiry.Tiers.${tier}`)}
          </span>
          <span className={cn(SMALL_TEXT_CLASS, "tw:text-[#4E4E4E]")}>
            <TierPriceLines rates={rates} />
          </span>
        </span>
      </span>
      <Divider />
      <TierDescription serviceKey={serviceKey} tier={tier} />
    </motion.button>
  );
};

/** The only tier a service offers, shown as information rather than a choice. */
const FixedTierCard = ({ serviceKey, tier, rates }) => {
  const { t } = useTranslation();
  return (
    <div className="tw:flex tw:flex-col tw:gap-y-[22px] tw:p-5 tw:lg:p-6 tw:bg-white tw:!rounded-[5px] tw:border tw:border-solid tw:border-[#282828]">
      <div className="tw:flex tw:flex-col tw:sm:flex-row tw:sm:items-center tw:sm:justify-between tw:gap-2 tw:font-hanken-grotesk">
        <span className="tw:font-bold tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">
          {t(`meetAssistInquiry.Tiers.${tier}`)}
        </span>
        <span className="tw:text-[14px] tw:leading-[20px] tw:text-[#4E4E4E] tw:sm:text-right">
          <TierPriceLines rates={rates} />
        </span>
      </div>
      <Divider />
      <TierDescription serviceKey={serviceKey} tier={tier} />
    </div>
  );
};

/**
 * "Arrival Meet & Assist Service Option": two cards to choose from, or one
 * fixed card when the service has a single tier (stations, connecting).
 */
export const ServiceOptionField = ({ serviceKey, selectedTier, sitePrices, onSelectTier }) => {
  const { t } = useTranslation();
  const { tiers, journey } = SERVICE_CONFIG[serviceKey];
  const isChoice = tiers.length > 1;
  const label = isChoice
    ? t(`meetAssistInquiry.ServiceOption.ChoiceLabel.${journey}`)
    : t(`meetAssistInquiry.ServiceOption.FixedLabel.${journey}`);

  return (
    <div className="tw:flex tw:flex-col tw:gap-y-[10px]">
      <FieldLabel label={label} isRequired={isChoice} />
      {isChoice ? (
        <div role="radiogroup" aria-label={label} className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-[300px_1fr] tw:gap-[10px]">
          {tiers.map((tier) => (
            <SelectableTierCard
              key={tier}
              serviceKey={serviceKey}
              tier={tier}
              rates={getTierRates(serviceKey, tier, sitePrices)}
              isSelected={tier === selectedTier}
              onSelect={() => onSelectTier(tier)}
            />
          ))}
        </div>
      ) : (
        <FixedTierCard serviceKey={serviceKey} tier={tiers[0]} rates={getTierRates(serviceKey, tiers[0], sitePrices)} />
      )}
    </div>
  );
};
